import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X, ShieldAlert, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { cn } from '../../lib/utils';

export const SafetyGate = ({
  isOpen,
  onClose,
  onConfirm,
  safetyNotice,
  mode = 'full',
  risk = 'medium'
}) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = () => {
    if (confirmed) {
      onConfirm();
      setConfirmed(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        data-testid="safety-gate-modal"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full sm:max-w-md bg-surface border border-border overflow-hidden rounded-t-lg sm:rounded-none max-h-[90vh] overflow-y-auto"
        >
          {/* Hazard stripes header */}
          <div className="h-2 hazard-stripes" />

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-warning/10 flex items-center justify-center flex-shrink-0">
                  <ShieldAlert className="w-5 h-5 sm:w-6 sm:h-6 text-warning" />
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold uppercase text-white">
                    SAFETY WARNING
                  </h3>
                  <span className="font-mono text-[10px] sm:text-xs text-warning">
                    {mode === 'diagnosis-only' ? 'REPAIR BLOCKED' : 'REVIEW BEFORE PROCEEDING'}
                  </span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-text-muted hover:text-white transition-colors p-1"
                data-testid="close-safety-gate"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Safety notice */}
            <div className="bg-warning/5 border border-warning/20 p-3 sm:p-4">
              <div className="flex items-start gap-2 sm:gap-3">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-text-secondary leading-relaxed" data-testid="safety-notice">
                  {safetyNotice || 'Please review safety considerations before proceeding with repair.'}
                </p>
              </div>
            </div>

            {/* Confirmation checkbox */}
            {mode === 'full' && (
              <label className="flex items-start gap-2 sm:gap-3 cursor-pointer group">
                <Checkbox
                  checked={confirmed}
                  onCheckedChange={setConfirmed}
                  className="mt-0.5 border-warning data-[state=checked]:bg-warning data-[state=checked]:border-warning"
                  data-testid="safety-checkbox"
                />
                <span className="text-xs sm:text-sm text-text-secondary group-hover:text-white transition-colors leading-relaxed">
                  I understand the safety risks involved and will take appropriate precautions.
                  I accept full responsibility for any damage or injury that may occur.
                </span>
              </label>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 pt-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1 border-border hover:border-text-muted"
                data-testid="cancel-safety-btn"
              >
                Cancel
              </Button>

              {mode === 'full' ? (
                <Button
                  onClick={handleConfirm}
                  disabled={!confirmed}
                  className={cn(
                    "flex-1 bg-warning text-black hover:bg-warning/90 font-heading uppercase",
                    !confirmed && "opacity-50 cursor-not-allowed"
                  )}
                  data-testid="confirm-safety-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Proceed
                </Button>
              ) : (
                <Button
                  onClick={onClose}
                  className="flex-1 bg-primary text-white hover:bg-primary/90 font-heading uppercase"
                  data-testid="understand-btn"
                >
                  I Understand
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SafetyGate;
